import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, MapPin, Star, BookmarkCheck, MessageSquare } from 'lucide-react'
import { useLanguage } from '../../i18n/index.jsx'
import { useSiteFilterStore } from '../../store/siteFilterStore.js'
import { useSites } from '../../hooks/useData.js'
import { useCommsSearch } from '../../hooks/useCommsSearch.js'
// Brighella v2 (ADR-013 #1 + #3 + P8) — Cmd+K acts as primary nav AND the
// front door to watchlist + saved-view actions. Two new namespaces:
//   >pin     pin the highlighted result (or list current watchlist)
//   >view    jump to a saved view (own + tenant-shared) with state applied
import { useCohesionStore } from '../../store/cohesionStore'
import type { WatchlistEntityType } from '../../store/cohesionStore'
import { useSavedViewsStore } from '../../store/savedViewsStore'
import { viewToPath } from '../ui/SavedViewMenu'
import type { SavedView } from '../../store/savedViewsStore'

// Surfaces whose saved views Cmd+K can search. `>view` loads each one's
// views from the store on open. Keep in sync with routable hub surfaces.
const VIEW_SURFACES = ['calendar']

interface SearchResult {
  name: string
  type: string
  id?: number | string
  icon?: React.ReactNode
  _entityType?: string
  _entityId?: number | string
  _path?: string
  [key: string]: unknown
}

interface CmdKSearchProps {
  cmdKOpen: boolean
  setCmdKOpen: (open: boolean) => void
  cmdKRef: React.RefObject<HTMLInputElement | null>
  searchQuery: string
  onSearch: (query: string) => void
  searchResults: SearchResult[]
  searchHighlight: number
  setSearchHighlight: (h: number | ((prev: number) => number)) => void
  onCmdKSelect: (result: SearchResult) => void
}

export default function CmdKSearch({
  cmdKOpen, setCmdKOpen, cmdKRef,
  searchQuery, onSearch, searchResults,
  searchHighlight, setSearchHighlight,
  onCmdKSelect,
}: CmdKSearchProps) {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const { setSiteIds, trackRecent } = useSiteFilterStore()
  const { data: sites = [] } = useSites()
  const watchlist = useCohesionStore((s) => s.watchlist)
  const unpinWatchlist = useCohesionStore((s) => s.unpinWatchlist)
  const savedViewsBySurface = useSavedViewsStore((s) => s.views)
  const loadViews = useSavedViewsStore((s) => s.loadViews)

  // When the palette opens, warm the saved-view cache so `>view` has data.
  useEffect(() => {
    if (cmdKOpen) VIEW_SURFACES.forEach((sf) => loadViews(sf))
  }, [cmdKOpen, loadViews])

  // useCommsSearch is a HOOK, so it must run on EVERY render — BEFORE the
  // `!cmdKOpen` early return below (Rules of Hooks). A previous version placed
  // it after the early return, so closing the palette dropped the hook and React
  // threw "Rendered fewer hooks than expected". Results are gated by isCommsNs.
  const isCommsNs = startsWithNs(searchQuery, ['>comms', '>c'])
  const commsRaw = isCommsNs ? stripNs(searchQuery, ['>comms', '>c']) : ''
  const { results: commsResults } = useCommsSearch(commsRaw, 'all')

  if (!cmdKOpen) return null

  // ── Namespace detection ──────────────────────────────────
  // Three namespaces today: site / pin / view. Each accepts a long form
  // (`>site `) and the short alias of its first letter (`>s `, `>p `, `>v `).
  const isSiteNs  = startsWithNs(searchQuery, ['>site', '>s'])
  const isPinNs   = startsWithNs(searchQuery, ['>pin',  '>p'])
  const isViewNs  = startsWithNs(searchQuery, ['>view', '>v'])

  let displayResults: SearchResult[] = searchResults
  let namespaceHint: string | null = null

  if (isSiteNs) {
    const raw = stripNs(searchQuery, ['>site', '>s'])
    const filtered = raw
      ? sites.filter(s => (s.name ?? '').toLowerCase().includes(raw.toLowerCase()))
      : sites
    displayResults = filtered.map(s => ({ icon: <MapPin size={14} />, name: s.name ?? String(s.id), type: 'site', id: s.id }))
    namespaceHint = t('cmdKSiteHint')
  } else if (isPinNs) {
    // `>pin` lists the current watchlist; selecting a row unpins it.
    const raw = stripNs(searchQuery, ['>pin', '>p']).toLowerCase()
    const filtered = raw
      ? watchlist.filter(w => (w.label || '').toLowerCase().includes(raw))
      : watchlist
    displayResults = filtered.map(w => ({
      icon: <Star size={14} />,
      name: w.label,
      type: 'watchlist',
      id: `${w.type}:${w.id}`,
      _entityType: w.type,
      _entityId: w.id,
    }))
    namespaceHint = t('cmdKPinHint')
  } else if (isViewNs) {
    // `>view` lists saved views (own + tenant-shared) across routable hub
    // surfaces; selecting one navigates to its surface with state applied.
    const raw = stripNs(searchQuery, ['>view', '>v']).toLowerCase()
    const allViews: SavedView[] = VIEW_SURFACES.flatMap((sf) => savedViewsBySurface[sf] || [])
    const filtered = raw
      ? allViews.filter(v => (v.name || '').toLowerCase().includes(raw))
      : allViews
    displayResults = filtered.map(v => ({
      icon: <BookmarkCheck size={14} />,
      name: v.is_own ? v.name : `${v.name} · ${v.owner_name}`,
      type: 'view',
      id: v.id,
      _path: viewToPath(v),
    }))
    namespaceHint = t('cmdKViewHint')
  } else if (isCommsNs) {
    // `>comms` / `>c` searches people, conversations, and messages via
    // useCommsSearch. Selecting navigates into the shell.
    const people = commsResults.people.map((p) => ({
      icon: <MessageSquare size={14} />,
      name: p.username,
      type: 'comms-person',
      id: p.id,
      _path: '/comms/chat',
    }))
    const convos = commsResults.conversations.map((c) => ({
      icon: <MessageSquare size={14} />,
      name: c.title || t('comms.dock.conversation'),
      type: 'comms-conv',
      id: c.id,
      _path: `/comms/chat/${c.id}`,
    }))
    const msgs = commsResults.messages.map((m) => ({
      icon: <MessageSquare size={14} />,
      name: m.body.length > 60 ? `${m.body.slice(0, 59)}…` : m.body,
      type: 'comms-msg',
      id: m.id,
      _path: `/comms/chat/${m.conversation_id}`,
    }))
    displayResults = [...people, ...convos, ...msgs]
    namespaceHint = t('cmdKCommsHint')
  }

  const handleSelect = (result: SearchResult) => {
    if (result.type === 'site') {
      setSiteIds([String(result.id)])
      trackRecent(result.id as string | number)
      setCmdKOpen(false)
      onSearch('')
    } else if (result.type === 'watchlist') {
      unpinWatchlist(result._entityType as WatchlistEntityType, result._entityId as string | number)
      // Stay open so the user can unpin multiple in a row.
    } else if (result.type === 'view') {
      navigate(result._path || '/calendar')
      setCmdKOpen(false)
      onSearch('')
    } else {
      onCmdKSelect(result)
    }
  }

  const showHint = !searchQuery

  return (
    <div role="presentation" style={{
      position: 'fixed', inset: 0, zIndex: 'var(--z-fullscreen, 3000)',
      background: 'transparent', display: 'flex',
      justifyContent: 'center', paddingTop: '15vh',
    }} onClick={() => setCmdKOpen(false)}>
      <div role="presentation" style={{
        background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)',
        width: '100%', maxWidth: 520, maxHeight: '60vh',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Search size={18} style={{ opacity: 0.5 }} />
          <input
            ref={cmdKRef}
            type="text"
            placeholder={t('searchPlaceholder')}
            value={searchQuery}
            aria-label={t('searchPlaceholder')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => { onSearch(e.target.value); setSearchHighlight(-1) }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setSearchHighlight(h => Math.min(h + 1, displayResults.length - 1)) }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setSearchHighlight(h => Math.max(h - 1, 0)) }
              else if (e.key === 'Enter' && searchHighlight >= 0 && displayResults[searchHighlight]) { e.preventDefault(); handleSelect(displayResults[searchHighlight]) }
              else if (e.key === 'Escape') { setCmdKOpen(false) }
            }}
            style={{
              flex: 1, border: 'none', outline: 'none', fontSize: 'var(--text-md)',
              background: 'transparent', color: 'var(--text-primary)',
            }}
          />
          <kbd style={{ fontSize: 'var(--text-tiny)', padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>ESC</kbd>
        </div>
        <div aria-live="polite" aria-atomic="true" style={{
          position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
          overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0,
        }}>
          {searchQuery.length >= 2 && `${displayResults.length} ${t('resultsFound')}`}
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {displayResults.length > 0 ? displayResults.map((r, i) => (
            <div key={i}
              role="button"
              tabIndex={0}
              onClick={() => handleSelect(r)}
              onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelect(r) } }}
              className={`cmdk-result${i === searchHighlight ? ' cmdk-result--highlight' : ''}`}
              onMouseEnter={() => setSearchHighlight(i)}
            >
              <span className="cmdk-result-icon">{r.icon}</span>
              <span className="cmdk-result-name">{r.name}</span>
              <span className="cmdk-result-type">{r.type}</span>
            </div>
          )) : (isSiteNs || isPinNs || isViewNs || isCommsNs) ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
              {t('noResults')}
            </div>
          ) : searchQuery.length >= 2 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
              {t('noResults')}
            </div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
              {t('typeToSearch')}
            </div>
          )}
        </div>
        {(showHint || namespaceHint) && (
          <div style={{ padding: '8px 16px', borderTop: '1px solid var(--border-subtle)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
            {namespaceHint || t('cmdKSiteHint')}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Namespace helpers ──────────────────────────────────────
// startsWithNs('>pin foo', ['>pin', '>p']) === true
function startsWithNs(query: string, aliases: string[]): boolean {
  for (const a of aliases) {
    if (query === a) return true
    if (query.startsWith(a + ' ')) return true
  }
  return false
}

// stripNs('>pin foo', ['>pin', '>p']) === 'foo'
function stripNs(query: string, aliases: string[]): string {
  for (const a of aliases) {
    if (query === a) return ''
    if (query.startsWith(a + ' ')) return query.slice(a.length + 1).trim()
  }
  return ''
}
